import { useState } from "react";
import { useTranslation } from "react-i18next";
import i18n, { type SupportedLanguage } from "../i18n";
import { TRANSLATED_LANGUAGES, languagePickerAvailable } from "../lib/languages";
import { api } from "../api/client";
import { announceUserPatch } from "../auth/useCurrentUser";
import { theme, card, btnPrimary } from "../theme";

/**
 * The signed-in person's display language. It belongs to the person, not to the plan, so it lives on
 * My Profile (every member can reach it) as well as on Services & billing.
 */
export function LanguagePreference({ marginBottom = 20 }: { marginBottom?: number }) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<SupportedLanguage>((i18n.language as SupportedLanguage) ?? "en");

  const save = async (lang: SupportedLanguage) => {
    setSaving(true); setSaved(false); setError("");
    try {
      await api.patch("/users/me/language", { language: lang });
      await i18n.changeLanguage(lang);
      setSelected(lang);
      announceUserPatch({ language: lang });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError("Could not save your language. Please try again.");
    } finally {
      setSaving(false);
    }
  };
  const unchanged = selected === i18n.language;

  // One complete translation (English) means there is nothing to choose: say so instead of a one-button picker.
  if (!languagePickerAvailable()) {
    return (
      <div style={{ ...card, marginBottom }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>{t("language.label")}</div>
        <div style={{ fontSize: 12, color: theme.color.subtle }}>The platform is available in English only for now. Other languages will appear here once their translation is complete.</div>
      </div>
    );
  }

  return (
    <div style={{ ...card, marginBottom }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>{t("language.label")}</div>
      <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 16 }}>{t("language.description")}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8, marginBottom: 14 }}>
        {TRANSLATED_LANGUAGES.map((lang) => {
          const active = selected === lang;
          return (
            <button
              key={lang}
              onClick={() => setSelected(lang)}
              style={{
                padding: "8px 12px", borderRadius: 3,
                border: `1.5px solid ${active ? theme.color.accent : theme.color.border}`,
                background: active ? "var(--c-accentBg)" : "transparent",
                color: active ? theme.color.accentText : theme.color.text,
                fontSize: 13, fontWeight: active ? 600 : 400, cursor: "pointer", textAlign: "left",
              }}
            >
              {t(`language.${lang}`)}
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button
          onClick={() => save(selected)}
          disabled={saving || unchanged}
          style={{ ...btnPrimary, opacity: saving || unchanged ? 0.55 : 1, cursor: saving || unchanged ? "default" : "pointer", fontSize: 12 }}
        >
          {saving ? t("common.saving") : t("common.save")}
        </button>
        {saved && <span style={{ fontSize: 12, color: theme.color.accentText }}>{t("language.saved")}</span>}
        {error && <span style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</span>}
      </div>
    </div>
  );
}
